
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaPhone,
  FaEnvelope,
  FaUser,
  FaCreditCard,
} from "react-icons/fa";

import {
  useGetCoachingCentersQuery,
  useDeleteCoachingCenterMutation,
} from "../../slices/coachingCenterApiSlice";

const CoachingCenterListScreen = () => {
  const navigate = useNavigate();

  const {
    data: centers,
    isLoading,
    error,
    refetch,
  } = useGetCoachingCentersQuery();

  const [deleteCoachingCenter, { isLoading: loadingDelete }] =
    useDeleteCoachingCenterMutation();

  const deleteHandler = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this coaching center?")
    ) {
      try {
        await deleteCoachingCenter(id).unwrap();

        toast.success("Coaching center deleted successfully");

        refetch();
      } catch (err) {
        toast.error(
          err?.data?.message ||
            err?.error ||
            "Failed to delete coaching center",
        );
      }
    }
  };

  if (isLoading) {
    return <div>Loading coaching centers...</div>;
  }

  if (error) {
    return <div>{error?.data?.message || error.error}</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-800">Coaching Centers</h1>

          <p className="text-gray-500 mt-2">
            Manage all registered coaching centers
          </p>
        </div>

        <Link
          to="/coaching-centers/create"
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
        >
          <FaPlus />
          Create Coaching Center
        </Link>
      </div>

      {loadingDelete && (
        <div className="mb-5 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
          Deleting coaching center...
        </div>
      )}

      {centers?.length === 0 ? (
        <div className="rounded-3xl bg-white p-12 shadow-xl text-center">
          <h2 className="text-2xl font-semibold text-gray-700">
            No Coaching Centers Found
          </h2>

          <p className="mt-3 text-gray-500">
            Click the button above to create your first coaching center.
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {centers.map((center) => (
            <div
              key={center._id}
              className="rounded-3xl bg-white/90 backdrop-blur shadow-xl border border-gray-100 p-6 hover:shadow-2xl transition"
            >
              <div className="flex flex-col lg:flex-row lg:justify-between gap-6">
                {/* Left */}
                <div className="flex-1">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {center.name}
                    </h2>

                    <span
                      className={`rounded-full px-4 py-1 text-sm font-semibold ${
                        center.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {center.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mt-6 text-gray-600">
                    <div className="flex items-center gap-3">
                      <FaUser className="text-blue-600" />
                      <span>{center.contactPerson}</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <FaPhone className="text-green-600" />
                      <span>{center.phone}</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <FaEnvelope className="text-red-500" />
                      <span>{center.email || "N/A"}</span>
                    </div>

                    <div>
                      <span className="font-semibold">Subscription:</span>{" "}
                      {center.subscriptionPlan || "N/A"}
                    </div>

                    <div>
                      <span className="font-semibold">Expiry:</span>{" "}
                      {center.subscriptionExpiry
                        ? new Date(
                            center.subscriptionExpiry,
                          ).toLocaleDateString()
                        : "N/A"}
                    </div>

                    <div>
                      <span className="font-semibold">Created By:</span>{" "}
                      {center.createdBy?.name || "-"}
                    </div>
                  </div>

                  {center.address && (
                    <div className="mt-5">
                      <p className="font-semibold text-gray-700">Address</p>
                      <p className="text-gray-600 mt-1">{center.address}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col gap-3 lg:w-44">
                  <button
                    onClick={() => navigate(`/coaching-centers/${center._id}`)}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 transition"
                  >
                    <FaEye />
                    View
                  </button>

                  <button
                    onClick={() =>
                      navigate(`/coaching-centers/${center._id}/edit`)
                    }
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-white hover:bg-amber-600 transition"
                  >
                    <FaEdit />
                    Edit
                  </button>

                  {/* Billing Settings */}
                  <button
                    onClick={() => navigate(`/billing/settings/${center._id}`)}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-white hover:bg-indigo-700 transition"
                  >
                    <FaCreditCard />
                    Billing
                  </button>

                  <button
                    onClick={() => deleteHandler(center._id)}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-white hover:bg-red-700 transition"
                  >
                    <FaTrash />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachingCenterListScreen;
